import { useState } from 'react';
import type { Squad, SquadPlayer, SquadValidation } from '@fantasy/types';
import type { UnmatchedScreenshotPlayer } from '../api.ts';
import { EntryIdImport } from './EntryIdImport.tsx';
import { ScreenshotImport } from './ScreenshotImport.tsx';

type ImportMethod = 'screenshot' | 'entry-id';

interface ImportSquadProps {
  gameweek: number;
  onImported: (squad: Squad, validation: SquadValidation) => void;
  /** See `ScreenshotImport`'s own doc comment for why this is handed
   * through unchanged — only the screenshot method ever produces a partial
   * match; the entry-id method is always exact or a clear failure. */
  onPartialImport: (matched: SquadPlayer[], unmatched: UnmatchedScreenshotPlayer[]) => void;
  /** Whether the accordion starts open. App.tsx passes `true` only for a
   * manager with no squad yet, where this is the primary thing to do —
   * `false` once one exists, where it's a rarely-needed re-import, not the
   * first thing worth seeing. Native `<details>` still lets the manager
   * toggle it themselves either way. */
  defaultOpen: boolean;
}

/**
 * One "Import a squad" section with two ways in — a screenshot upload or a
 * real FPL entry id — rather than two separate accordions for what's
 * conceptually one action. Screenshot is the default method: it was this
 * app's original, more-established entry point, and switching to entry id
 * is a single click away for anyone who wants it. Each method owns its own
 * form/preview state; switching between them doesn't lose anything since
 * they're independent components, just unmounted while not selected.
 */
export function ImportSquad({ gameweek, onImported, onPartialImport, defaultOpen }: ImportSquadProps) {
  const [method, setMethod] = useState<ImportMethod>('screenshot');

  return (
    <details className="panel panel--accent library" open={defaultOpen}>
      <summary className="library-summary">
        <span>
          <span className="library-chevron" aria-hidden="true">
            ▸
          </span>
          Import a squad
        </span>
      </summary>

      <div className="library-body">
        <div className="row row--flush" role="group" aria-label="Import method">
          <button
            type="button"
            className="ghost small"
            aria-pressed={method === 'screenshot'}
            onClick={() => setMethod('screenshot')}
          >
            From a screenshot
          </button>
          <button
            type="button"
            className="ghost small"
            aria-pressed={method === 'entry-id'}
            onClick={() => setMethod('entry-id')}
          >
            From an FPL entry id
          </button>
        </div>

        {method === 'screenshot' ? (
          <ScreenshotImport gameweek={gameweek} onImported={onImported} onPartialImport={onPartialImport} />
        ) : (
          <EntryIdImport onImported={onImported} />
        )}
      </div>
    </details>
  );
}
